import { AuthenticationProvider } from '~/enums/AuthenticationProvider';
import { ClientSyncType } from '~/enums/ClientSyncType';
import { UserManagement } from '~/enums/UserManagement';
import { Client } from '~/features/admin/types';
import { pilot } from '~/services/serviceInstances';
import { ServerClient } from '~/types/Client';

type GetClientsParams = {
  page?: number;
  searchText?: string;
};

type Response = {
  results: ServerClient[];
  count: number;
  next: string | null;
  previous: string | null;
};

export const getClients = async ({
  page = 1,
  searchText = '',
}: GetClientsParams): Promise<{
  results: Client[];
  count: number;
}> => {
  const response: Response = await pilot.get(
    `client/list/?page=${page}&name=${searchText}`,
  );

  return {
    results: transformResponse(response.results),
    count: response.count,
  };
};

const transformResponse = (clients: ServerClient[]): Client[] => {
  return clients.map((client) => ({
    id: client.id,
    name: client.name,
    subDomain: client.sub_domain,
    storageProvider: client.storage_provider,
    bucket: client.bucket,
    index: client.index,
    descrip: client.descrip,
    clientType: client.client_type,
    docCount: client.doc_count,
    preSigDocCount: client.pre_sig_doc_count,
    postSigDocCount: client.post_sig_doc_count,
    docLimit: client.doc_limit ?? 0,
    restorationHubCount: client.restoration_hub_count,
    billableDocCount: client.billable_doc_count,
    clauseCountLimit: client.clause_count_limit,
    xrayPromptModelQuota: client.xray_prompt_model_quota,
    xrayBillingType: client.xray_billing_type,
    ssoEnabled: client.sso_enabled,
    workflowEnabled: client.workflow_enabled,
    searchV2Enabled: client.search_v2_enabled,
    inboundEmailEnabled: client.inbound_email_enabled,
    exportExcelEnabled: client.export_excel_enabled,
    externalApiEnabled: client.external_api_enabled,
    automationSmartClauseFullEnabled:
      client.automation_smart_clause_full_enabled,
    automationSmartClauseApiEnabled: client.automation_smart_clause_api_enabled,
    documentGroupRevampEnabled: client.document_group_revamp_enabled,
    bulkDocumentMetadataEnabled: client.bulk_document_metadata_enabled,
    bulkDocumentMetadataFilenameUpdateEnabled:
      client.bulk_document_metadata_filename_update_enabled,
    bulkDocumentMetadataFileMoveEnabled:
      client.bulk_document_metadata_file_move_enabled,
    bulkDocumentMetadataMultipleTasksEnabled:
      client.bulk_document_metadata_multiple_tasks_enabled,
    allowClauseRenamingEnabled: client.allow_clause_renaming_enabled,
    esWeightedSearchEnabled: client.es_weighted_search_enabled,
    featureFlagMappedFields: client.feature_flag_mapped_fields ?? [],
    customAlgorithms: client.custom_algorithms,
    allowedSyncFileTypes: client.allowed_sync_file_types,
    provisionTypes: client.provision_types,
    disableDocTagging: client.disable_doc_tagging,
    inactiveTimeoutInSeconds: client.inactive_timeout_in_seconds,
    sessionTimeoutInSeconds: client.session_timeout_in_seconds,
    tenantId: client.tenant_id,
    tenantEnv: client.tenant_env,
    suvId: client.suv_id,
    cpsHost: client.cps_host,
    wssHost: client.wss_host,
    galahadHost: client.galahad_host,
    homepageHost: client.homepage_host,
    awsAccountId: client.aws_account_id,
    customerBillingId: client.customer_billing_id,
    syncType: client.sync_type as ClientSyncType,
    recordId: client.record_id,
    documentDeleteReasonRequired: client.document_delete_reason_required,
    automationTrainKafkaEnabled: client.automation_train_kafka_enabled,
    dashboardEnabled: client.dashboard_enabled,
    editClauseEnabled: client.edit_clause_enabled,
    hideExpirationsEnabled: client.hide_expirations_enabled,
    oodocsEditingEnabled: client.oodocs_editing_enabled,
    oodocsEnabled: client.oodocs_enabled,
    pdfPreviewHighlightingEnabled: client.pdf_preview_highlighting_enabled,
    syncPairWebhookEnabled: client.sync_pair_webhook_enabled,
    userManagement: client.user_management as UserManagement,
    authenticationProvider:
      client.authentication_provider as AuthenticationProvider,
  }));
};
